import { CircularProgress, IconButton } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faComments, faPlus, faChevronRight, faChevronDown, faStar, faSearch, faTimes, faSortAmountDown } from '@fortawesome/free-solid-svg-icons';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useAiAssistant } from '../../AiAssistantContext';
import * as api from '../../api';
import { DEFAULT_MODEL, ISession } from '../../types';
import SessionSettingsModal from './SessionSettingsModal';

interface SessionsPanelProps {
  collapsed: boolean;
  onToggle: () => void;
}

const SessionsPanel: React.FC<SessionsPanelProps> = ({ collapsed, onToggle }) => {
  const { state, dispatch } = useAiAssistant();
  const projectId = state.selectedProjectId;
  const projectSessions = state.sessions.filter((s) => s.projectId === projectId);
  const [settingsSession, setSettingsSession] = useState<ISession | null>(null);
  const [creating, setCreating] = useState(false);
  const [hoveredSessionId, setHoveredSessionId] = useState<number | null>(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filterText, setFilterText] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);
  const [sortMode, setSortMode] = useState<'default' | 'name' | 'newest' | 'oldest'>('default');
  const favSessionIds = state.favoriteSessionsByProject[projectId!] ?? [];

  const sortLabels = { default: 'Favorites first', name: 'Name A-Z', newest: 'Newest first', oldest: 'Oldest first' };
  const cycleSortMode = () => {
    const modes: Array<typeof sortMode> = ['default', 'name', 'newest', 'oldest'];
    setSortMode(modes[(modes.indexOf(sortMode) + 1) % modes.length]);
  };

  if (!projectId) return null;

  const handleNewSession = async () => {
    if (!projectId || creating) return;
    setCreating(true);
    try {
      const title = `Chat ${projectSessions.length + 1}`;
      await api.createSession(projectId, { title, model: DEFAULT_MODEL });
      const sessions = await api.fetchSessions(projectId);
      dispatch({ type: 'SET_SESSIONS', sessions });
      if (sessions.length > 0) {
        const newest = sessions[0];
        dispatch({
          type: 'OPEN_TAB',
          tab: { id: `session:${newest.id}`, type: 'session', label: newest.title, sessionId: newest.id, projectId },
        });
      }
    } catch {
      // Session creation failed silently — user can retry
    }
    setCreating(false);
  };

  const handleOpenSession = (sessionId: number, title: string) => {
    dispatch({
      type: 'OPEN_TAB',
      tab: { id: `session:${sessionId}`, type: 'session', label: title, sessionId, projectId: projectId! },
    });
  };

  return (
    <div>
      <SectionHeader onClick={onToggle}>
        <HeaderLeft>
          <ChevronIcon icon={collapsed ? faChevronRight : faChevronDown} />
          <SectionTitle>Sessions</SectionTitle>
        </HeaderLeft>
        <HeaderActions>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); cycleSortMode(); }} title={`Sort: ${sortLabels[sortMode]}`}>
            <FontAwesomeIcon icon={faSortAmountDown} size="xs" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowFilter(!showFilter); if (showFilter) setFilterText(''); else setTimeout(() => filterInputRef.current?.focus(), 0); }} title="Filter">
            <FontAwesomeIcon icon={faSearch} size="xs" />
          </IconButton>
          {creating ? (
            <CircularProgress size={14} />
          ) : (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleNewSession(); }} title="New session">
              <FontAwesomeIcon icon={faPlus} size="xs" />
            </IconButton>
          )}
        </HeaderActions>
      </SectionHeader>

      {!collapsed && showFilter && (
        <FilterRow>
          <FilterInput
            ref={filterInputRef}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter sessions..."
            onKeyDown={(e) => { if (e.key === 'Escape') { setShowFilter(false); setFilterText(''); } }}
          />
          {filterText && (
            <IconButton size="small" onClick={() => setFilterText('')}>
              <FontAwesomeIcon icon={faTimes} size="xs" />
            </IconButton>
          )}
        </FilterRow>
      )}

      {!collapsed && sortMode !== 'default' && (
        <SortLabel>{sortLabels[sortMode]}</SortLabel>
      )}

      {!collapsed && (
        <>
          {state.loadingSessions && (
            <LoadingRow>
              <CircularProgress size={14} />
            </LoadingRow>
          )}

          {!state.loadingSessions && projectSessions.length === 0 && (
            <EmptyText>No sessions yet. <EmptyAction onClick={handleNewSession}>Create one</EmptyAction></EmptyText>
          )}

          {[...projectSessions].filter((s) => { if (!filterText) return true; const title = s.title.toLowerCase(); return filterText.toLowerCase().split(/\s+/).filter(Boolean).every((w) => title.includes(w)); }).sort((a, b) => {
            if (sortMode === 'default') {
              const aFav = favSessionIds.includes(a.id) ? 0 : 1;
              const bFav = favSessionIds.includes(b.id) ? 0 : 1;
              return aFav - bFav;
            }
            if (sortMode === 'name') return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
            if (sortMode === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          }).map((session) => {
            const isOpen = state.tabs.some((t) => t.id === `session:${session.id}`);
            const isUnread = state.unreadSessionIds.includes(session.id);
            const isFav = favSessionIds.includes(session.id);
            const isHovered = hoveredSessionId === session.id;
            const icon = isHovered ? faStar : faComments;
            const iconColor = isFav ? '#ffc107' : '#90a4ae';
            return (
              <SessionRow
                key={session.id}
                $active={isOpen}
                onClick={() => handleOpenSession(session.id, session.title)}
                onMouseEnter={() => setHoveredSessionId(session.id)}
                onMouseLeave={() => setHoveredSessionId(null)}
              >
                <FavIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({ type: 'TOGGLE_FAVORITE_SESSION', sessionId: session.id, projectId: projectId! });
                  }}
                >
                  <FontAwesomeIcon icon={icon} size="xs" style={{ color: iconColor }} />
                </FavIcon>
                {isUnread && <UnreadDot />}
                <SessionName title={session.title} $unread={isUnread}>{session.title}</SessionName>
                <SessionActions>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSettingsSession(session);
                    }}
                  >
                    <FontAwesomeIcon icon={faCog} size="xs" />
                  </IconButton>
                </SessionActions>
              </SessionRow>
            );
          })}
        </>
      )}

      {settingsSession && (
        <SessionSettingsModal
          session={settingsSession}
          onClose={() => setSettingsSession(null)}
          onSave={(updated) => {
            dispatch({ type: 'UPDATE_SESSION', session: updated });
            setSettingsSession(null);
          }}
          onDelete={(sessionId) => {
            dispatch({ type: 'DELETE_SESSION', sessionId });
            setSettingsSession(null);
          }}
        />
      )}
    </div>
  );
};

export default SessionsPanel;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  min-height: 30px;
  padding: 2px 2px 2px 8px;
  background: #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: #e0e0e0;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  font-size: 10px;
  color: #555;
  width: 10px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  ${SectionHeader}:hover & {
    opacity: 1;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 4px 4px;
`;

const FilterInput = styled.input`
  font-size: 13px;
  border: 1px solid #bbb;
  border-radius: 3px;
  padding: 3px 6px;
  outline: none;
  flex: 1;
  min-width: 0;
  &:focus {
    border-color: #1976d2;
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  color: #555;
  letter-spacing: 0.5px;
`;

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 6px 0;
`;

const SortLabel = styled.div`
  font-size: 10px;
  color: #1976d2;
  padding: 0 4px 2px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const EmptyText = styled.div`
  font-size: 12px;
  color: #999;
  padding: 4px 0;
`;

const EmptyAction = styled.span`
  color: #1976d2;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const SessionRow = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px;
  background: ${(p) => (p.$active ? '#e3f2fd' : 'transparent')};
  &:hover {
    background: ${(p) => (p.$active ? '#e3f2fd' : '#f0f0f0')};
  }
`;

const FavIcon = styled.span`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  margin-right: 6px;
`;

const UnreadDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1976d2;
  flex-shrink: 0;
  margin-right: 4px;
`;

const SessionName = styled.span<{ $unread?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  font-weight: ${(p) => (p.$unread ? 600 : 'normal')};
`;

const SessionActions = styled.div`
  display: flex;
  opacity: 0;
  ${SessionRow}:hover & {
    opacity: 1;
  }
`;
